import { Card } from '@quero-delivery/quero-components-web';
import Tooltip from 'rc-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import {
  getEndOfDay,
  getStartOfDay,
  subDays,
  toDate,
} from '@querodelivery/qd-date-helper';
import { useNavigate } from 'react-router';

import { ButtonSeeAllList } from '@/_features/motoboys/motoboy-details/components/button-see-all-list';
import DeliveryCard from '@/Components/delivery/deliveryCard/DeliveryCard';
import { stringifyQueryStringToUrl } from '@/Utils/FnUtils';
import type { IMotoboy } from '@/Interfaces/Data';

import styles from './LastDeliveries.module.scss';
import type { IQueryString } from '@/_features/deliveries/types';
import { useGetDeliveriesList } from '@/_features/deliveries/api/get-deliveries';
import { Skeleton } from '@/_config/lib/react-native-skeleton';

interface LastDeliveriesProps {
  motoboy: IMotoboy;
}

const maxDeliveriesToShow = 3;

function LastDeliveries({ motoboy }: LastDeliveriesProps) {
  const navigate = useNavigate();

  const defaultLastDeliveries = {
    startDate: subDays(toDate(), 7),
    endDate: toDate(),
    limit: maxDeliveriesToShow,
  };

  const {
    data: motoboyDeliveries,
    isLoading: loadingMotoboyDeliveries,
    refetch,
  } = useGetDeliveriesList({
    limit: maxDeliveriesToShow,
    startDate: getStartOfDay(defaultLastDeliveries.startDate),
    endDate: getEndOfDay(defaultLastDeliveries.endDate),
    motoboyId: motoboy._id,
  });

  const haveDeliveries = motoboyDeliveries && motoboyDeliveries.data.length > 0;

  function handleOpenDeliveriesList() {
    const query: IQueryString = {
      startDate: defaultLastDeliveries.startDate.toISOString(),
      endDate: defaultLastDeliveries.endDate.toISOString(),
      motoboyId: motoboy._id,
      cityId: motoboy.municipioId,
    };

    const url = stringifyQueryStringToUrl(query, '/entregas');

    navigate(url);
    window.scrollTo({ top: 0 });
  }

  return (
    <Card type="none" className={styles.deliveriesList}>
      {loadingMotoboyDeliveries ? (
        <div className={styles.deliveriesSkeletonContainer}>
          <div className={styles.deliveriesSkeletonTitleContainer}>
            <Skeleton height={15} borderRadius={8} width="5vw" />
            <Skeleton height={15} borderRadius={8} width="10vw" />
          </div>
          <Skeleton height={164} borderRadius={8} style={{ flex: 1 }} />
        </div>
      ) : (
        <>
          <div className={styles.header}>
            <span>
              Últimas Entregas{' '}
              <Tooltip overlay="Entregas no últimos 7 dias">
                <FontAwesomeIcon icon={faQuestionCircle} />
              </Tooltip>
            </span>

            <span>
              {motoboyDeliveries?.totalCount}{' '}
              {motoboyDeliveries?.totalCount == 1 ? 'entrega' : 'entregas'}
            </span>
          </div>

          {haveDeliveries ? (
            motoboyDeliveries.data
              .slice(0, maxDeliveriesToShow)
              .map(delivery => (
                <DeliveryCard
                  key={delivery._id}
                  delivery={delivery}
                  rechargeDeliveries={refetch}
                />
              ))
          ) : (
            <p className="body2 text-center">Nenhuma entrega encontrada</p>
          )}
          {motoboyDeliveries &&
          motoboyDeliveries.data.length >= maxDeliveriesToShow ? (
            <ButtonSeeAllList onClick={handleOpenDeliveriesList} />
          ) : null}
        </>
      )}
    </Card>
  );
}

export default LastDeliveries;
